/**
 * ProviderAnalytics
 * Detailed analytics with date range picker, charts, geographic breakdown, and export.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import api from '../api';

const RANGES = [
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: '90d', label: '90 Days' },
  { key: 'custom', label: 'Custom' },
];

const PROVINCES_MAP = {
  ON: 'Ontario', QC: 'Quebec', BC: 'British Columbia', AB: 'Alberta',
  MB: 'Manitoba', SK: 'Saskatchewan', NS: 'Nova Scotia', NB: 'New Brunswick',
  NL: 'Newfoundland', PE: 'PEI', NT: 'NWT', NU: 'Nunavut', YT: 'Yukon',
};

function generateTimeSeriesData(days) {
  const data = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    data.push({
      date: d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
      pageViews: Math.floor(50 + Math.random() * 150),
      searchAppearances: Math.floor(100 + Math.random() * 300),
      ctr: parseFloat((2 + Math.random() * 8).toFixed(1)),
    });
  }
  return data;
}

function generateTrustComparison() {
  const months = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];
  return months.map((month) => ({
    month,
    yourScore: Math.floor(72 + Math.random() * 10),
    sectorAvg: Math.floor(65 + Math.random() * 8),
  }));
}

function generateSearchTerms() {
  return [
    { term: 'best healthcare provider Toronto', visits: 142 },
    { term: 'affordable health insurance Ontario', visits: 98 },
    { term: 'medical services comparison Canada', visits: 87 },
    { term: 'top rated healthcare plans', visits: 65 },
    { term: 'health provider reviews near me', visits: 54 },
    { term: 'Canadian healthcare comparison', visits: 43 },
    { term: 'private healthcare Ontario', visits: 38 },
    { term: 'best medical plans 2026', visits: 31 },
  ];
}

function generateGeoData() {
  return [
    { province: 'ON', visitors: 1842 },
    { province: 'QC', visitors: 624 },
    { province: 'BC', visitors: 518 },
    { province: 'AB', visitors: 431 },
    { province: 'MB', visitors: 187 },
    { province: 'NS', visitors: 142 },
    { province: 'SK', visitors: 98 },
    { province: 'NB', visitors: 67 },
    { province: 'NL', visitors: 45 },
    { province: 'PE', visitors: 23 },
  ];
}

function getDaysForRange(range) {
  switch (range) {
    case '7d': return 7;
    case '30d': return 30;
    case '90d': return 90;
    default: return 30;
  }
}

function exportCSV(data, filename) {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]).join(',');
  const rows = data.map((r) => Object.values(r).join(','));
  const csv = [headers, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ProviderAnalytics() {
  const [range, setRange] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const days = getDaysForRange(range);

  // Fetch analytics
  const { data: analyticsRaw } = useQuery({
    queryKey: ['provider-analytics', range],
    queryFn: () => api.get(`/api/provider-portal/analytics/overview?range=${range}`).catch(() => null),
    placeholderData: null,
  });

  const timeSeriesData = analyticsRaw?.timeSeries ?? generateTimeSeriesData(days);
  const trustComparison = analyticsRaw?.trustComparison ?? generateTrustComparison();
  const searchTerms = analyticsRaw?.searchTerms ?? generateSearchTerms();
  const geoData = analyticsRaw?.geoBreakdown ?? generateGeoData();

  const totalVisitors = useMemo(() => geoData.reduce((sum, g) => sum + g.visitors, 0), [geoData]);

  const handleExportReport = useCallback(() => {
    exportCSV(timeSeriesData, `analytics-report-${range}-${new Date().toISOString().split('T')[0]}.csv`);
  }, [timeSeriesData, range]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
            Analytics
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
            Detailed performance metrics for your provider listing.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportReport}
          style={{
            padding: '10px 20px', fontSize: '13px', fontWeight: '600', borderRadius: '8px',
            border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer',
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Date range picker */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRange(r.key)}
            style={{
              padding: '6px 14px', fontSize: '13px', fontWeight: '600', borderRadius: '8px',
              border: range === r.key ? 'none' : '1px solid #d1d5db',
              backgroundColor: range === r.key ? '#3b82f6' : '#fff',
              color: range === r.key ? '#fff' : '#475569',
              cursor: 'pointer',
            }}
          >
            {r.label}
          </button>
        ))}
        {range === 'custom' && (
          <>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
            <span style={{ color: '#94a3b8' }}>to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
          </>
        )}
      </div>

      {/* Page views + search appearances chart */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          Page Views & Search Appearances
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={timeSeriesData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} interval={Math.max(1, Math.floor(days / 10))} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={{ fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Line type="monotone" dataKey="pageViews" stroke="#3b82f6" strokeWidth={2} dot={false} name="Page Views" />
            <Line type="monotone" dataKey="searchAppearances" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Search Appearances" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* CTR chart */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          Click-Through Rate (%)
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={timeSeriesData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} interval={Math.max(1, Math.floor(days / 10))} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} unit="%" />
            <Tooltip contentStyle={{ fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }} formatter={(v) => [`${v}%`, 'CTR']} />
            <Line type="monotone" dataKey="ctr" stroke="#10b981" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Trust score comparison */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          Trust Score: You vs Sector Average
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={trustComparison}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis domain={[50, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={{ fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Bar dataKey="yourScore" fill="#3b82f6" name="Your Score" radius={[4, 4, 0, 0]} />
            <Bar dataKey="sectorAvg" fill="#cbd5e1" name="Sector Average" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom row: search terms + geo breakdown */}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {/* Top search terms */}
        <div style={{ flex: '1 1 350px', minWidth: '300px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Top Search Terms
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: '#64748b', fontWeight: '600' }}>Search Term</th>
                <th style={{ textAlign: 'right', padding: '8px 10px', color: '#64748b', fontWeight: '600' }}>Visits</th>
              </tr>
            </thead>
            <tbody>
              {searchTerms.map((t, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', color: '#1e293b' }}>{t.term}</td>
                  <td style={{ padding: '8px 10px', color: '#475569', textAlign: 'right', fontWeight: '600' }}>{t.visits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Geographic breakdown */}
        <div style={{ flex: '1 1 300px', minWidth: '280px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Visitors by Province
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: '#64748b', fontWeight: '600' }}>Province</th>
                <th style={{ textAlign: 'right', padding: '8px 10px', color: '#64748b', fontWeight: '600' }}>Visitors</th>
                <th style={{ textAlign: 'right', padding: '8px 10px', color: '#64748b', fontWeight: '600' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {geoData.map((g) => (
                <tr key={g.province} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', color: '#1e293b' }}>{PROVINCES_MAP[g.province] || g.province}</td>
                  <td style={{ padding: '8px 10px', color: '#475569', textAlign: 'right', fontWeight: '600' }}>{g.visitors.toLocaleString('en-CA')}</td>
                  <td style={{ padding: '8px 10px', color: '#94a3b8', textAlign: 'right' }}>
                    {totalVisitors > 0 ? ((g.visitors / totalVisitors) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
